import { useEffect, useRef, useState } from "react";
import { ChefHat, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { usePoll } from "@/hooks/usePoll";
import { PageHeader, Empty } from "@/components/common";
import TicketCard, { useTick } from "@/components/TicketCard";

let audioCtx = null;
function beep() {
  if (!audioCtx) return;
  const t = audioCtx.currentTime;
  [0, 0.25, 0.5].forEach((d, i) => {
    const o = audioCtx.createOscillator();
    const g = audioCtx.createGain();
    o.type = "square";
    o.frequency.value = i === 2 ? 1320 : 880;
    g.gain.setValueAtTime(0.0001, t + d);
    g.gain.exponentialRampToValueAtTime(0.35, t + d + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + d + 0.2);
    o.connect(g).connect(audioCtx.destination);
    o.start(t + d);
    o.stop(t + d + 0.22);
  });
}

function useNewOrderAlert(rows, enabled) {
  const seen = useRef(null);
  useEffect(() => {
    if (!rows) return;
    const ids = new Set(rows.flatMap((b) => b.items.map((i) => i.id)));
    if (seen.current) {
      const fresh = rows.filter((b) => b.items.some((i) => !seen.current.has(i.id)));
      if (fresh.length) {
        if (enabled) beep();
        toast.info(`Order baru: ${fresh.map((b) => `${b.code} (${b.table_name})`).join(", ")}`);
      }
    }
    seen.current = ids;
  }, [rows, enabled]);
}

export default function Dapur() {
  const { data, reload } = usePoll("/kitchen/bills", 5000);
  const [sound, setSound] = useState(false);
  useTick();
  useNewOrderAlert(data, sound);
  const rows = data || [];

  const toggleSound = async () => {
    if (!sound) {
      audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
      await audioCtx.resume();
      beep();
    }
    setSound(!sound);
  };

  return (
    <div>
      <PageHeader title="Dashboard Dapur" subtitle="Klik menu yang sudah keluar, lalu klik Selesai jika satu bill sudah komplit">
        <button data-testid="kitchen-sound-toggle" onClick={toggleSound} className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors active:scale-95 ${sound ? "bg-emerald-600 text-white" : "border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100"}`}>
          {sound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}{sound ? "Suara Aktif" : "Aktifkan Suara"}
        </button>
        <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-800" data-testid="kitchen-count">{rows.length} bill antre</span>
      </PageHeader>
      {!sound && <p className="mb-4 rounded-xl bg-orange-50 px-4 py-2.5 text-sm text-orange-800" data-testid="kitchen-sound-hint">Klik "Aktifkan Suara" sekali agar bunyi peringatan berbunyi setiap ada order baru (aturan browser).</p>}
      {rows.length === 0 && <Empty icon={ChefHat} text="Tidak ada order di dapur" testId="kitchen-empty" />}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {rows.map((b) => (
          <TicketCard key={b.id} bill={b} prefix="kitchen"
            isChecked={(i) => i.kitchen_ready}
            canToggle={(i) => !i.delivered}
            toggleUrl={(i) => `/bills/${b.id}/items/${i.id}/kitchen`}
            doneUrl={`/bills/${b.id}/kitchen-done`}
            doneLabel="Selesai"
            onChange={reload} />
        ))}
      </div>
    </div>
  );
}
